import { useState } from "react";
import styles from "../../styles/welcome.module.css";
import Modal from "../modal/Modal";
import { modalType } from "../../types/modal";
import { getUserDevice } from "../../utils/helper";
import { useClient } from "../../provider/ClientProvider";
import { useTranslation } from "react-i18next";
import Alert from "./Alert";
import ClientCodeStep from "./ClientCodeStep";
import IssuerStep from "./IssuerStep";
import EmailStep from "./EmailStep";

/**
 * The landing page — everything before capture, on one screen.
 *
 * This used to be three separate pages: enter a code, choose an issuer, then pick
 * how to proceed. That is three page loads of friction before an applicant has
 * done anything. It is now one card that reveals the next thing needed:
 *
 *   1. a code, with the option to hand off to a phone instead
 *   2. an issuer, but only when the code named an organisation with several
 *   3. an email address, then straight into capture
 *
 * The card, its heading and any error stay put across all three, so the applicant
 * sees a form filling in rather than pages replacing each other.
 *
 * The two modals live here rather than in the steps: the QR hand-off belongs to
 * the first stage and the privacy policy to the last, and holding both here keeps
 * the consent state in one place.
 */
const Landing = () => {
  const { t } = useTranslation();
  const { client, issuerChoice, error } = useClient();

  function showQRModal() {
    if (sessionStorage.getItem("showQRPopup")) {
      return (
        getUserDevice() === "DESKTOP" &&
        sessionStorage.getItem("showQRPopup") === "true"
      );
    }
    return false;
  }

  const [qrVisible, setQrVisible] = useState(showQRModal());
  const [privacyVisible, setPrivacyVisible] = useState(false);
  const [consented, setConsented] = useState(false);

  // A resolved client wins over a pending issuer choice, so picking an issuer
  // moves straight on rather than briefly showing the list again.
  const stage = client ? "email" : issuerChoice ? "issuer" : "code";

  const heading =
    stage === "email"
      ? t("email.header")
      : stage === "issuer"
        ? t("issuer.header")
        : t("clientid.form.header");

  const intro =
    stage === "email"
      ? client?.displayName
        ? t("email.description", { issuer: client.displayName })
        : t("email.description.fallback")
      : stage === "issuer"
        ? `${issuerChoice?.tenantName}. ${t("issuer.description")}`
        : t("clientid.description");

  return (
    <section className={styles.section}>
      <div className={styles.container}>
        <div className={styles.rightSection}>
          <div>
            <h2 className={styles.h2}>{heading}</h2>
            <p className={styles.cardIntro}>{intro}</p>
          </div>

          {error && <Alert>{error}</Alert>}

          {stage === "code" && <ClientCodeStep onVerifyOnMobile={() => setQrVisible(true)} />}
          {stage === "issuer" && <IssuerStep />}
          {stage === "email" && (
            <EmailStep
              consented={consented}
              onConsentChange={() => setConsented((prev) => !prev)}
              onOpenPrivacyPolicy={() => setPrivacyVisible(true)}
            />
          )}
        </div>
      </div>

      <Modal
        type={modalType.QR_CODE}
        isOpen={qrVisible}
        onClose={() => setQrVisible(false)}
        actions={{ continue: () => setQrVisible(false) }}
      />
      <Modal
        type={modalType.PRIVACY_POLICY}
        isOpen={privacyVisible}
        onClose={() => setPrivacyVisible(false)}
        actions={{
          continue: () => {
            setConsented(true);
            setPrivacyVisible(false);
          },
          isAccepted: consented,
        }}
      />
    </section>
  );
};

export default Landing;
